/**
 * Delivery calendar (A09/A11 epics) via /v1/calendar/items: Monday-Sunday week, event cards coloured by delivery stage,
 * filters (channel = item type, stage), Calendar/Timeline switch and a "Due next" panel. Owner, channel and due date come from
 * the deliverable behind the item (/v1/deliverables/:id); the API exposes no per-item owner for other item types and no tags,
 * so those rows are not rendered. Times are shown in the user's time zone (QM-D08-003).
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { EmptyState, ErrorState, Heading, Loading, PageHeader } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { BffError, bffJson } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { useResource, type ResourceState } from '../../../lib/resource';
import { localDate, type CalItem } from '../home/home-data';
import { useWorkspaceName } from '../_shared/ListScreen';
import '../_shared/customer.css';
import {
  addDays, bucketOf, canRequestApproval, chainSteps, channelOptions, deliverableId, filterItems, groupByDay, mondayOf, nextDue, NO_FILTERS, ownerOptions, queryRange, STATUS_BUCKETS, weekDates, withOwnerLabels,
  type CalFilters, type OwnedItem,
} from './calendar-data';

interface DeliverableView { id: string; ownerUserId?: string; approverUserId?: string | null; channel?: string | null; dueAt?: string | null; state?: string }
interface Member { id: string; userId?: string; email?: string; displayName?: string | null; name?: string | null }

export function CalendarPage() {
  const { t, locale, localePrefs, me } = useApp();
  const f = useFormat();
  const on = !!me?.tenantId;
  const tz = localePrefs.timezone;
  const today = localDate(new Date(), tz);
  const [start, setStart] = useState(() => mondayOf(today));
  const [view, setView] = useState<'calendar' | 'timeline'>('calendar');
  const [filters, setFilters] = useState<CalFilters>(NO_FILTERS);
  const [selected, setSelected] = useState<string | null>(null);
  const range = queryRange(start);
  const res = useResource<CalItem[] | { items?: CalItem[] }>(on ? `/v1/calendar/items?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}&tz=${encodeURIComponent(tz)}` : null);
  const dates = weekDates(start);
  const rawRows = rowsOf(res.status === 'ready' ? res.data : null) as OwnedItem[];
  const hasOwners = rawRows.some((i) => !!i.ownerId);
  const roster = useResource<{ members?: Member[] }>(on && hasOwners ? '/v1/team/members' : null);
  const weekRows = hasOwners && roster.status === 'ready' ? withOwnerLabels(rawRows, roster.data.members ?? []) : rawRows;
  const owners = ownerOptions(weekRows);
  const fmt = (date: string, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale, { ...o, timeZone: 'UTC', numberingSystem: localePrefs.numberingSystem }).format(new Date(`${date}T12:00:00Z`));
  return (
    <section className="qm-page" aria-labelledby="page-title">
      <PageHeader eyebrow={useWorkspaceName()} title={t('calendar.delivery.title')} intro={t('shell.screens.calendar.intro')} />
      <div className="qs-split">
        <div className="cu-main">
          <div className="cu-toolbar">
            <div className="qs-filters" role="group" aria-label={t('calendar.delivery.filters')}>
              <label className="cu-field"><span>{t('calendar.delivery.channel')}</span>
                <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
                  <option value="">{t('calendar.delivery.all')}</option>
                  {channelOptions(weekRows).map((k) => <option key={k} value={k}>{t(`calendar.type.${k}`)}</option>)}
                </select>
              </label>
              <label className="cu-field"><span>{t('calendar.delivery.status')}</span>
                <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                  <option value="">{t('calendar.delivery.all')}</option>
                  {STATUS_BUCKETS.map((k) => <option key={k} value={k}>{t(`calendar.stage.${k}`)}</option>)}
                </select>
              </label>
              <label className="cu-field"><span>{t('calendar.delivery.owner')}</span>
                <select value={filters.owner ?? ''} disabled={owners.length === 0} aria-describedby={owners.length === 0 ? 'cal-owner-hint' : undefined} onChange={(e) => setFilters({ ...filters, owner: e.target.value })}>
                  <option value="">{t('calendar.delivery.all')}</option>
                  {owners.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                {owners.length === 0 ? <small id="cal-owner-hint" className="cu-hint">{t('calendar.delivery.needsOwner')}</small> : null}
              </label>
              <button type="button" className="cu-btn" data-variant="outline" data-testid="calendar-clear" onClick={() => setFilters(NO_FILTERS)}>{t('calendar.delivery.clear')}</button>
              <div className="qs-seg" role="group" aria-label={t('calendar.delivery.view')}>
                <button type="button" aria-pressed={view === 'calendar'} onClick={() => setView('calendar')}>{t('calendar.delivery.viewCalendar')}</button>
                <button type="button" aria-pressed={view === 'timeline'} onClick={() => setView('timeline')}>{t('calendar.delivery.viewTimeline')}</button>
              </div>
            </div>
            <div className="cu-btnrow" role="group" aria-label={t('calendar.delivery.weekNav')}>
              <button type="button" className="cu-btn" data-variant="outline" onClick={() => setStart(addDays(start, -7))}>{t('calendar.delivery.prev')}</button>
              <button type="button" className="cu-btn" data-variant="outline" onClick={() => setStart(mondayOf(today))}>{t('calendar.delivery.today')}</button>
              <button type="button" className="cu-btn" data-variant="outline" onClick={() => setStart(addDays(start, 7))}>{t('calendar.delivery.next')}</button>
            </div>
          </div>
          <p className="cu-hint"><bdi>{fmt(dates[0]!, { day: 'numeric', month: 'short' })}</bdi> - <bdi>{fmt(dates[6]!, { day: 'numeric', month: 'short', year: 'numeric' })}</bdi> · {t('calendar.timezone', { timezone: tz })}</p>
          <CalendarBody res={res} dates={dates} today={today} filters={filters} view={view} selected={selected} onSelect={setSelected} fmt={fmt} f={f} />
          <ul className="qs-legend" aria-label={t('calendar.delivery.legend')}>
            {STATUS_BUCKETS.map((k) => <li key={k}><span className="cu-legend-dot" data-status={k} aria-hidden="true" />{t(`calendar.stage.${k}`)}</li>)}
          </ul>
          <p className="cu-hint">{t('calendar.delivery.hint')}</p>
        </div>
        <DuePanel res={res} filters={filters} selected={selected} onReload={res.reload} />
      </div>
    </section>
  );
}

const rowsOf = (d: CalItem[] | { items?: CalItem[] } | null): CalItem[] => (Array.isArray(d) ? d : d?.items ?? []);

type Fmt = (date: string, o: Intl.DateTimeFormatOptions) => string;

function CalendarBody({ res, dates, today, filters, view, selected, onSelect, fmt, f }: {
  res: ResourceState<CalItem[] | { items?: CalItem[] }>; dates: string[]; today: string; filters: CalFilters; view: 'calendar' | 'timeline'; selected: string | null;
  onSelect: (id: string) => void; fmt: Fmt; f: ReturnType<typeof useFormat>;
}) {
  const { t } = useApp();
  if (res.status === 'loading') return <Loading label={t('common.loading')} />;
  if (res.status === 'error') return <ErrorState message={t('shell.error.load')} />;
  const week = rowsOf(res.data).filter((i) => dates.includes(i.local.date));
  if (!week.length) return <EmptyState title={t('shell.screens.calendar.empty')} />;
  const days = groupByDay(filterItems(week, filters), dates);
  const card = (i: CalItem) => {
    const b = bucketOf(i);
    return (
      <button key={i.id} type="button" className="qs-ev" data-status={b ?? undefined} aria-pressed={selected === i.id} onClick={() => onSelect(i.id)}>
        <time dateTime={i.startsAt}>{i.local.time}</time>
        <span><bdi>{f.text(i.title)}</bdi></span>
        <small>{b ? t(`calendar.stage.${b}`) : t(`calendar.type.${i.type}`)}{i.overdue ? ` · ${t('calendar.overdue')}` : ''}</small>
      </button>
    );
  };
  if (view === 'timeline') {
    const filled = days.filter((d) => d.items.length);
    return filled.length ? (
      <ol className="cu-timeline">
        {filled.map((d) => (
          <li key={d.date} data-today={d.date === today ? 'true' : undefined}>
            <h3>{fmt(d.date, { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
            <ul>{d.items.map((i) => <li key={i.id}>{card(i)}</li>)}</ul>
          </li>
        ))}
      </ol>
    ) : <EmptyState title={t('calendar.delivery.noMatch')} />;
  }
  return (
    <div className="cu-week">
      {days.map((d) => (
        <section key={d.date} className="cu-day" data-today={d.date === today ? 'true' : undefined} aria-label={fmt(d.date, { weekday: 'long', day: 'numeric', month: 'long' })}>
          <div className="cu-day__head" aria-hidden="true"><span>{fmt(d.date, { weekday: 'short' })}</span><b>{fmt(d.date, { day: 'numeric' })}</b></div>
          {d.items.length ? d.items.map(card) : <p className="cu-day__none">{t('calendar.delivery.none')}</p>}
        </section>
      ))}
    </div>
  );
}

function DuePanel({ res, filters, selected, onReload }: { res: ResourceState<CalItem[] | { items?: CalItem[] }>; filters: CalFilters; selected: string | null; onReload: () => void }) {
  const { t } = useApp();
  const f = useFormat();
  const all = res.status === 'ready' ? rowsOf(res.data) : [];
  const item = all.find((i) => i.id === selected) ?? nextDue(filterItems(all, filters), new Date());
  const dlvId = item ? deliverableId(item) : null;
  const dlv = useResource<DeliverableView>(dlvId ? `/v1/deliverables/${encodeURIComponent(dlvId)}` : null);
  const team = useResource<{ members?: Member[] }>(dlvId ? '/v1/team/members' : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const nameOf = (id?: string | null) => {
    if (!id) return '';
    const m = team.status === 'ready' ? (team.data.members ?? []).find((x) => x.id === id || x.userId === id) : undefined;
    return m?.displayName || m?.name || m?.email || '';
  };
  const request = async () => {
    if (!item || !dlvId) return;
    setBusy(true); setError(null);
    try {
      await bffJson(`/v1/deliverables/${encodeURIComponent(dlvId)}/transitions`, 'POST', { to: 'client_review' });
      setSent(item.id);
      onReload();
    } catch (e) {
      setError(e instanceof BffError && e.status === 403 ? t('shell.approval.noPermission') : t('shell.error.action'));
    } finally { setBusy(false); }
  };
  const bucket = item ? bucketOf(item) : null;
  const steps = chainSteps(bucket);
  const d = dlv.status === 'ready' ? dlv.data : null;
  const owner = nameOf(d?.ownerUserId);
  const approver = nameOf(d?.approverUserId);
  return (
    <aside className="qs-aside" aria-labelledby="due-title" data-testid="calendar-due">
      <div className="qs-aside__head"><Heading level={2} id="due-title">{selected && item ? t('calendar.delivery.details') : t('calendar.delivery.dueNext')}</Heading></div>
      {res.status === 'loading' ? <Loading label={t('common.loading')} /> : !item ? <p className="cu-hint">{t('calendar.delivery.dueEmpty')}</p> : (
        <>
          <p><strong><bdi>{f.text(item.title)}</bdi></strong></p>
          <dl className="cu-kv">
            <dt>{t('calendar.delivery.channel')}</dt><dd>{d?.channel ? <bdi>{d.channel}</bdi> : t(`calendar.type.${item.type}`)}</dd>
            <dt>{t('calendar.delivery.due')}</dt><dd>{f.dateTime(item.startsAt)}</dd>
            {owner ? <><dt>{t('calendar.delivery.owner')}</dt><dd><bdi>{owner}</bdi></dd></> : null}
            {approver ? <><dt>{t('calendar.delivery.approver')}</dt><dd><bdi>{approver}</bdi></dd></> : null}
            <dt>{t('calendar.delivery.status')}</dt><dd>{bucket ? t(`calendar.stage.${bucket}`) : '-'}{item.overdue ? ` · ${t('calendar.overdue')}` : ''}</dd>
          </dl>
          {steps.length ? (
            <div>
              <p className="qs-micro">{t('calendar.delivery.chain')}</p>
              <ol className="qs-chain">
                {steps.map((s, n) => (
                  <li key={s.key} data-state={s.state}>
                    <span className="qs-chain__n" aria-hidden="true">{f.number(n + 1)}</span>
                    <span>{t(`calendar.stage.${s.key}`)}{s.state === 'current' ? <small>{t('calendar.delivery.current')}</small> : s.state === 'done' ? <small>{t('calendar.delivery.done')}</small> : null}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          {sent === item.id ? <p role="status" className="cu-hint">{t('calendar.delivery.requested')}</p> : null}
          {canRequestApproval(item) && sent !== item.id ? <button type="button" className="cu-btn" disabled={busy} onClick={() => void request()}>{t('calendar.delivery.request')}</button> : null}
          {item.type === 'approval' && dlvId ? <Link className="cu-btn" data-variant="outline" to="/approvals">{t('shell.home.priority.cta')}</Link> : null}
          {error ? <ErrorState message={error} /> : null}
        </>
      )}
    </aside>
  );
}
